/**
 * Alterações contratuais dos funcionários (o que o eSocial chama de S-2206): um registro com data de vigência, motivo e
 * TUDO o que muda de uma vez — cargo, salário, horário, tipo de contrato, local… e cláusulas livres. O registro guarda o
 * "de → para", atualiza o cadastro na data em que passa a valer e é de onde sai o aditivo (ou o novo contrato de trabalho).
 */
import { z } from 'zod';
import { DataISO, Id } from './comum.js';
import { ES_CATEGORIA, ES_REGIME_JORNADA, ES_TEMPO_PARCIAL, ES_TIPO_CONTRATO, ES_TIPO_JORNADA, ES_UNIDADE_SALARIO } from './esocial.js';
import { GradeHorario, descreverGrade } from './jornada.js';

export const MotivoAlteracao = z.enum(['PROMOCAO', 'REAJUSTE', 'CONVENCAO', 'MERITO', 'FUNCAO', 'HORARIO', 'TRANSFERENCIA', 'CONTRATO', 'CORRECAO', 'OUTRO']);
export type MotivoAlteracao = z.infer<typeof MotivoAlteracao>;
export const ROTULO_MOTIVO_ALTERACAO: Record<MotivoAlteracao, string> = {
  PROMOCAO: 'Promoção', REAJUSTE: 'Reajuste salarial', CONVENCAO: 'Convenção ou dissídio coletivo', MERITO: 'Aumento por mérito', FUNCAO: 'Mudança de cargo ou função', HORARIO: 'Mudança de horário',
  TRANSFERENCIA: 'Transferência de local ou setor', CONTRATO: 'Mudança no tipo ou prazo do contrato', CORRECAO: 'Correção de cadastro', OUTRO: 'Outro',
};

const texto = (max: number) => z.string().trim().max(max).nullable();
const codigo = (min: number, max: number) => z.number().int().min(min).max(max).nullable();

/** O que pode mudar. Chave PRESENTE = muda (null = fica em branco); chave ausente = não mexe. */
export const AlteracaoNovo = z.object({
  cargo: texto(80), cbo: texto(10), funcao: texto(100), cboFuncao: texto(10), setor: texto(60), localTrabalho: texto(200),
  salario: z.number().min(0).max(9_999_999).nullable(), unidadeSalario: codigo(1, 7), salarioVariavel: texto(255),
  categoria: codigo(101, 999), tipoContrato: codigo(1, 3), dataTermino: DataISO.nullable(),
  regimeJornada: codigo(1, 4), tipoJornada: codigo(2, 9), tempoParcial: codigo(0, 3), horarioNoturno: z.boolean(),
  gradeHorario: GradeHorario.nullable(), horasSemanais: z.number().min(0).max(99).nullable(), jornada: texto(999),
}).partial();
export type AlteracaoNovo = z.infer<typeof AlteracaoNovo>;
export type CampoAlteracao = keyof AlteracaoNovo;

export type GrupoAlteracao = 'Cargo e função' | 'Remuneração' | 'Contrato' | 'Horário';
/** onde o campo mora no cadastro: coluna do funcionário ou chave do bloco do eSocial */
export const CAMPOS_ALTERACAO: ReadonlyArray<{ campo: CampoAlteracao; rotulo: string; grupo: GrupoAlteracao; onde: 'coluna' | 'esocial' }> = [
  { campo: 'cargo', rotulo: 'Cargo', grupo: 'Cargo e função', onde: 'coluna' }, { campo: 'cbo', rotulo: 'CBO do cargo', grupo: 'Cargo e função', onde: 'coluna' },
  { campo: 'funcao', rotulo: 'Função', grupo: 'Cargo e função', onde: 'esocial' }, { campo: 'cboFuncao', rotulo: 'CBO da função', grupo: 'Cargo e função', onde: 'esocial' },
  { campo: 'setor', rotulo: 'Setor', grupo: 'Cargo e função', onde: 'coluna' }, { campo: 'localTrabalho', rotulo: 'Local de trabalho', grupo: 'Cargo e função', onde: 'esocial' },
  { campo: 'salario', rotulo: 'Salário', grupo: 'Remuneração', onde: 'coluna' }, { campo: 'unidadeSalario', rotulo: 'Unidade do salário', grupo: 'Remuneração', onde: 'esocial' },
  { campo: 'salarioVariavel', rotulo: 'Remuneração variável', grupo: 'Remuneração', onde: 'esocial' },
  { campo: 'categoria', rotulo: 'Categoria do trabalhador', grupo: 'Contrato', onde: 'esocial' }, { campo: 'tipoContrato', rotulo: 'Tipo de contrato', grupo: 'Contrato', onde: 'esocial' },
  { campo: 'dataTermino', rotulo: 'Término do contrato', grupo: 'Contrato', onde: 'coluna' },
  { campo: 'regimeJornada', rotulo: 'Regime de jornada', grupo: 'Horário', onde: 'esocial' }, { campo: 'tipoJornada', rotulo: 'Tipo de jornada', grupo: 'Horário', onde: 'esocial' },
  { campo: 'tempoParcial', rotulo: 'Tempo parcial', grupo: 'Horário', onde: 'esocial' }, { campo: 'horarioNoturno', rotulo: 'Horário noturno', grupo: 'Horário', onde: 'esocial' },
  { campo: 'gradeHorario', rotulo: 'Grade de horário', grupo: 'Horário', onde: 'coluna' }, { campo: 'horasSemanais', rotulo: 'Horas semanais', grupo: 'Horário', onde: 'coluna' },
  { campo: 'jornada', rotulo: 'Descrição da jornada', grupo: 'Horário', onde: 'coluna' },
];

const Clausula = z.string().trim().min(3).max(2000);
export const AlteracaoContratualBody = z.object({
  /** o dia em que passa a valer: até hoje, o cadastro muda na hora; no futuro, muda sozinho nesse dia */
  dataVigencia: DataISO,
  motivo: MotivoAlteracao.default('OUTRO'),
  /** título curto para o histórico ("Promoção a analista") — vazio = o sistema descreve pelo que mudou */
  descricao: z.string().trim().max(200).nullable().default(null),
  observacao: z.string().trim().max(2000).nullable().default(null),
  novo: AlteracaoNovo.default({}),
  /** registro do passado (um aumento antigo, uma promoção de anos atrás): entra no histórico e NÃO mexe no cadastro */
  soHistorico: z.boolean().default(false),
  /** cláusulas livres que entram no aditivo e não mexem no cadastro (vale-transporte, teletrabalho, banco de horas…) */
  clausulas: z.array(Clausula).max(20).default([]),
});
export type AlteracaoContratualBody = z.infer<typeof AlteracaoContratualBody>;

export const AlteracaoItem = z.object({ campo: z.string(), rotulo: z.string(), de: z.string().nullable(), para: z.string().nullable() });
export type AlteracaoItem = z.infer<typeof AlteracaoItem>;

export const AlteracaoContratual = z.object({
  id: Id, funcionarioId: Id, dataVigencia: DataISO, motivo: MotivoAlteracao, motivoRotulo: z.string(), descricao: z.string(), observacao: z.string().nullable(),
  itens: z.array(AlteracaoItem), clausulas: z.array(z.string()),
  /** AGENDADA = a vigência ainda não chegou e o cadastro ainda mostra o valor antigo */
  situacao: z.enum(['AGENDADA', 'APLICADA', 'HISTORICO']), aplicadaEm: z.string().nullable(),
  /** o que foi pedido, campo a campo — para reabrir a alteração agendada no formulário */
  novo: AlteracaoNovo,
  avisos: z.array(z.string()),
  /** o aditivo (ou contrato) gerado a partir deste registro e a assinatura do funcionário, se pedida */
  documento: z.object({ anexoId: Id, nome: z.string(), assinatura: z.enum(['PENDENTE', 'ASSINADO']).nullable() }).nullable(),
  /** veio de um reajuste coletivo */
  emLote: z.boolean(),
  criadoEm: z.string(), criadoPor: z.string().nullable(),
});
export type AlteracaoContratual = z.infer<typeof AlteracaoContratual>;

/** Reajuste coletivo: o mesmo percentual (ou valor) para vários funcionários de um cliente, cada um com o seu registro. */
export const ReajusteColetivoBody = z.object({
  dataVigencia: DataISO, motivo: MotivoAlteracao.default('CONVENCAO'),
  percentual: z.number().min(0.01).max(200),
  /** piso da categoria: quem ficar abaixo depois do percentual sobe até ele */
  piso: z.number().min(0).max(9_999_999).nullable().default(null),
  observacao: z.string().trim().max(2000).nullable().default(null),
  /** vazio = todos os ativos com salário */
  funcionarioIds: z.array(Id).max(2000).default([]),
  confirmar: z.boolean().default(false),
});
export type ReajusteColetivoBody = z.infer<typeof ReajusteColetivoBody>;
export const ReajusteColetivoResultado = z.object({
  total: z.number().int(), aplicaveis: z.number().int(), criadas: z.number().int(),
  linhas: z.array(z.object({ funcionarioId: Id, nome: z.string(), cargo: z.string().nullable(), de: z.number().nullable(), para: z.number().nullable(), situacao: z.enum(['OK', 'SEM_SALARIO', 'JA_TEM']), detalhe: z.string().nullable() })),
});
export type ReajusteColetivoResultado = z.infer<typeof ReajusteColetivoResultado>;

// ------------------------------------------------------------------ valores por escrito

const moeda = (n: number) => `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dataBr = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const daTabela = (tabela: ReadonlyArray<readonly [number, string]>, v: unknown) => tabela.find(([c]) => c === v)?.[1] ?? String(v);

/** O valor de um campo como a pessoa lê ("R$ 2.500,00", "Prazo indeterminado", "Segunda a sexta das 8h às 17h…"). Vazio = null. */
export function valorPorEscrito(campo: string, v: unknown): string | null {
  if (v == null || v === '' || (Array.isArray(v) && !v.length)) return null;
  switch (campo) {
    case 'salario': return moeda(Number(v));
    case 'horasSemanais': return `${String(Number(v)).replace('.', ',')} h`;
    case 'dataTermino': return dataBr(String(v));
    case 'horarioNoturno': return v ? 'Sim' : 'Não';
    case 'unidadeSalario': return daTabela(ES_UNIDADE_SALARIO, v);
    case 'categoria': return daTabela(ES_CATEGORIA, v);
    case 'tipoContrato': return daTabela(ES_TIPO_CONTRATO, v);
    case 'regimeJornada': return daTabela(ES_REGIME_JORNADA, v);
    case 'tipoJornada': return daTabela(ES_TIPO_JORNADA, v);
    case 'tempoParcial': return daTabela(ES_TEMPO_PARCIAL, v);
    case 'gradeHorario': { const g = GradeHorario.safeParse(v); return g.success ? descreverGrade(g.data) : null; }
    default: return String(v);
  }
}

/** Título do registro quando ninguém escreveu um: "Salário e cargo", "Horário", "3 cláusulas". */
export function resumoDosCampos(campos: string[], clausulas: number): string {
  const grupos: string[] = [];
  const tem = (...cs: string[]) => cs.some((c) => campos.includes(c));
  if (tem('salario', 'unidadeSalario', 'salarioVariavel')) grupos.push('salário');
  if (tem('cargo', 'cbo', 'funcao', 'cboFuncao')) grupos.push('cargo');
  if (tem('gradeHorario', 'horasSemanais', 'jornada', 'regimeJornada', 'tipoJornada', 'tempoParcial', 'horarioNoturno')) grupos.push('horário');
  if (tem('categoria', 'tipoContrato', 'dataTermino')) grupos.push('contrato');
  if (tem('setor', 'localTrabalho')) grupos.push('local de trabalho');
  if (clausulas) grupos.push(clausulas === 1 ? 'uma cláusula' : `${clausulas} cláusulas`);
  if (!grupos.length) return 'Alteração contratual';
  const t = grupos.length === 1 ? grupos[0]! : `${grupos.slice(0, -1).join(', ')} e ${grupos[grupos.length - 1]}`;
  return t.charAt(0).toUpperCase() + t.slice(1);
}
